import React, { useState, useEffect } from 'react';
import { GraduationCap, Lock, ChevronRight, Film, Palette, Share2, Coins, Cog, Briefcase, Building2, Sparkles, Clock } from 'lucide-react';

const FUTURE_TRACKS = [
  { icon: Film, label: 'Wideo AI' },
  { icon: Palette, label: 'Grafika AI' },
  { icon: Share2, label: 'Social media' },
  { icon: Coins, label: 'Zarabianie z AI' },
  { icon: Cog, label: 'Automatyzacja' },
  { icon: Briefcase, label: 'AI w pracy' },
  { icon: Building2, label: 'AI w biznesie' },
];

const calcTimeLeft = (targetDate) => {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

const CountdownDigit = ({ value, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className="w-[60px] h-[60px] sm:w-[68px] sm:h-[68px] rounded-2xl flex items-center justify-center"
      style={{
        background: 'linear-gradient(180deg, rgba(251,191,36,0.15) 0%, rgba(251,191,36,0.05) 100%)',
        border: '1px solid rgba(251,191,36,0.2)',
        boxShadow: '0 4px 16px rgba(251,191,36,0.08), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
    >
      <span className="text-2xl sm:text-3xl font-bold text-white tabular-nums">
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="text-[9px] text-slate-500 uppercase tracking-widest font-semibold">{label}</span>
  </div>
);

export const SpecTrainingSection = ({ user, onNavigatePaywall }) => {
  const isPremium = user?.subscription_status === 'active';
  const createdAt = user?.created_at;

  const unlockDate = createdAt
    ? new Date(new Date(createdAt).getTime() + 7 * 86400000).toISOString()
    : null;

  const [timeLeft, setTimeLeft] = useState(() => (unlockDate ? calcTimeLeft(unlockDate) : null));

  useEffect(() => {
    if (!unlockDate) return;
    const id = setInterval(() => setTimeLeft(calcTimeLeft(unlockDate)), 1000);
    return () => clearInterval(id);
  }, [unlockDate]);

  // Countdown visible for ALL users — acts as a hook
  const isCounting = timeLeft !== null;
  // Unlocked only for premium users after countdown ends
  const isUnlocked = isPremium && !isCounting;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(245,158,11,0.06) 50%, rgba(14,165,233,0.05) 100%)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(251,191,36,0.15)',
        boxShadow: '0 4px 32px rgba(251,191,36,0.06), inset 0 1px 0 rgba(255,255,255,0.03)',
      }}
      data-testid="spec-training-section"
    >
      <div className="px-5 py-6">
        {/* Header */}
        <div className="flex items-start gap-3.5 mb-2">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              boxShadow: '0 6px 20px rgba(245,158,11,0.3)',
            }}
          >
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-white leading-snug">
              Szkolenia specjalistyczne
            </p>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              {isUnlocked && 'Pierwsze szkolenie czeka na Ciebie'}
              {isCounting && isPremium && 'Twoje szkolenie jest w drodze'}
              {isCounting && !isPremium && 'Odliczanie do odblokowania'}
              {!isCounting && !isPremium && 'Wyłącznie w planie Premium'}
            </p>
          </div>
        </div>

        {/* ═══ STATE: Countdown active — visible for ALL users ═══ */}
        {isCounting && (
          <div className="my-5">
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <CountdownDigit value={timeLeft.days} label="dni" />
              <span className="text-amber-500/40 text-xl font-light pb-4">:</span>
              <CountdownDigit value={timeLeft.hours} label="godz" />
              <span className="text-amber-500/40 text-xl font-light pb-4">:</span>
              <CountdownDigit value={timeLeft.minutes} label="min" />
              <span className="text-amber-500/40 text-xl font-light pb-4">:</span>
              <CountdownDigit value={timeLeft.seconds} label="sek" />
            </div>
            
            {isPremium ? (
              <p className="text-center text-xs text-slate-500 mt-3">
                Szkolenie odblokuje się automatycznie po zakończeniu odliczania
              </p>
            ) : (
              <button
                onClick={onNavigatePaywall}
                className="w-full mt-4 rounded-xl px-4 py-3.5 flex items-center gap-3.5 transition-all group"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                data-testid="spec-training-upgrade-btn"
              >
                <Lock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-white">Odblokuj z Premium</p>
                  <p className="text-xs text-slate-400 mt-0.5">Uzyskaj dostęp zaraz po zakończeniu odliczania</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400 transition-all group-hover:translate-x-0.5 flex-shrink-0" />
              </button>
            )}
          </div>
        )}

        {/* ═══ STATE: Premium user, unlocked ═══ */}
        {isUnlocked && (
          <div
            className="my-5 rounded-xl px-4 py-5 text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(251,191,36,0.12), rgba(245,158,11,0.06))',
              border: '1px solid rgba(251,191,36,0.18)',
            }}
          >
            <Sparkles className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-sm font-bold text-amber-300">Szkolenie odblokowane</p>
            <p className="text-xs text-slate-400 mt-1">Rozpocznij szkolenie i rozwiń nowe umiejętności</p>
          </div>
        )}

        {/* ═══ STATE: Free user, countdown finished ═══ */}
        {!isPremium && !isCounting && (
          <div className="my-5">
            <button
              onClick={onNavigatePaywall}
              className="w-full rounded-xl px-4 py-4 flex items-center gap-3.5 transition-all group"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              data-testid="spec-training-upgrade-btn"
            >
              <Lock className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-white">Dostępne w planie Premium</p>
                <p className="text-xs text-slate-400 mt-0.5">Odblokuj szkolenia specjalistyczne</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400 transition-all group-hover:translate-x-0.5 flex-shrink-0" />
            </button>
          </div>
        )}

        {/* Future tracks */}
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-2.5">
            {isPremium ? 'Twoje specjalizacje' : 'Nadchodzące specjalizacje'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {FUTURE_TRACKS.map((track) => {
              const Icon = track.icon;
              return (
                <div
                  key={track.label}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-400"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <Icon className="w-3 h-3 text-amber-400/60" />
                  {track.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Premium note */}
        <p className="text-[10px] text-slate-500/80 mt-3.5 leading-relaxed">
          {isPremium
            ? 'Twój plan Premium obejmuje dostęp do wszystkich szkoleń specjalistycznych.'
            : 'Plan Premium obejmuje nieograniczony dostęp do szkoleń specjalistycznych publikowanych w PromptOrigin.'}
        </p>
      </div>
    </div>
  );
};

export default SpecTrainingSection;
